import { AnalyticsEventName } from '@dupe/common/constants'
import { ChevronDownIcon } from 'lucide-react'
import { useRouter } from 'next/compat/router'
import { useState } from 'react'
import { isLibrarySearchEscape } from '~/src/components/library/LibraryParts'
import LibraryPanel, {
  type LibrarySource,
} from '~/src/components/library/LibraryPanel'
import { LIBRARY_LABEL } from '~/src/components/library/libraryModel'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/src/components/ui/popover'
import useAnalytics from '~/src/hooks/useAnalytics'
import { appRouterRoutePattern } from '~/src/hooks/useNavClickTracker'
import { cn } from '~/src/lib/utils'

/** The badge stops counting here. */
const BADGE_MAX = 9

/** Rows a page holds in the dropdown until its list is laid out. */
export const LIBRARY_DROPDOWN_PAGE_SIZE = 10

/** Badge text for a count of new searches: "3", or "9+" past nine. */
export const formatRecentsBadge = (count: number) =>
  count > BADGE_MAX ? `${BADGE_MAX}+` : String(count)

/** Screen-reader text for a count of new searches: "3 new searches". */
export const recentsCountLabel = (count: number) =>
  `${count} new ${count === 1 ? 'search' : 'searches'}`

/**
 * The red count pill, floated over the item's top-right corner so showing or
 * clearing it never changes the item's width (and the nav never shifts).
 */
export const RECENTS_BADGE_CLASSES =
  'pointer-events-none absolute -right-3 -top-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-xxs font-semibold leading-none text-white ring-2 ring-white dark:ring-black'

interface Props {
  /** New searches since the menu was last opened. 0 hides the badge. */
  count: number
  lists: LibrarySource
  /** Called when the menu opens: clears the badge and re-reads the lists. */
  onOpen: () => void
  className?: string
}

/**
 * Header "Library" item, beside the avatar, with a red badge counting new
 * searches. Opens a menu of Recents / Saves / Bottles tabs, each with filters
 * and a page of rows (`LibraryPanel`). The caller decides whether the item
 * shows (`useLibraryNav`).
 */
const LibraryNavButton: React.FC<Props> = ({
  count,
  lists,
  onOpen,
  className,
}) => {
  const analytics = useAnalytics()
  const router = useRouter()
  const [open, setOpen] = useState(false)

  const track = (
    label: string,
    extra: Record<string, unknown> = {},
    sendToPlausible = false
  ) =>
    analytics.track(
      AnalyticsEventName.NavLinkClicked,
      {
        location: 'header',
        href: null,
        label,
        recents_count: count,
        from_route:
          router?.pathname ?? appRouterRoutePattern(window.location.pathname),
        ...extra,
      },
      { skipGA: true, ...(sendToPlausible ? { sendToPlausible } : {}) }
    )

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          track(LIBRARY_LABEL)
          onOpen()
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={
            count > 0
              ? `${LIBRARY_LABEL}, ${recentsCountLabel(count)}`
              : LIBRARY_LABEL
          }
          className={cn(
            'group relative inline-flex shrink-0 items-center gap-1.5 text-base font-medium leading-6 tracking-[-0.1px]',
            'text-foreground-secondary transition-colors duration-200 ease-motion-expo hover:text-ink data-[state=open]:text-ink dark:hover:text-white dark:data-[state=open]:text-white',
            className
          )}
        >
          {LIBRARY_LABEL}
          <ChevronDownIcon
            aria-hidden
            className='size-4 transition-transform duration-200 group-data-[state=open]:rotate-180'
          />
          {count > 0 && (
            <span aria-hidden className={RECENTS_BADGE_CLASSES}>
              {formatRecentsBadge(count)}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        sideOffset={12}
        arrowClassName='hidden'
        collisionPadding={12}
        // A click shouldn't ring the first tab; keyboard users Tab into it.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Esc in the search box closes the search; the next Esc, the menu.
        onEscapeKeyDown={(e) => {
          if (isLibrarySearchEscape(e)) e.preventDefault()
        }}
        className='flex max-h-[min(calc(100dvh-6rem),46rem)] w-[min(26rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-black/5 bg-white p-2 shadow-lg dark:border-white/10 dark:bg-neutral-900'
      >
        <LibraryPanel
          {...lists}
          idPrefix='library-dropdown'
          className='min-h-0 flex-1'
          pageSize={LIBRARY_DROPDOWN_PAGE_SIZE}
          onTab={(tab) => track(`${LIBRARY_LABEL} tab`, { section: tab })}
          onFilter={(tab, filter) =>
            track(`${LIBRARY_LABEL} filter`, { section: tab, filter })
          }
          onPage={(tab, page) =>
            track(`${LIBRARY_LABEL} page`, { section: tab, page: page + 1 })
          }
          onPick={(tab, entry, index) => {
            track(
              `${LIBRARY_LABEL} item`,
              { section: tab, href: entry.href, position: index },
              !!entry.external
            )
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export default LibraryNavButton
